import { useState } from "react";
import {
  Button,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  InputField,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Divider,
} from "@jem-hr/jem-ui";
import { FileText, Send, Download, CheckCircle2, Clock, AlertCircle } from "lucide-react";
import { BranchFilter } from "../components/BranchFilter";

// ─── Types ───────────────────────────────────────────────────────────────────

interface Employee {
  id: string;
  name: string;
  employeeNumber: string;
  employeeType: "Full-time" | "Part-time";
  department: string;
  status: "Completed" | "Pending" | "Overdue";
  lastUpdated?: string;
  completionDate?: string;
  policies: string[];
}

// ─── Data ────────────────────────────────────────────────────────────────────

const employeesData: Employee[] = [
  {
    id: "1",
    name: "Sarah Johnson",
    employeeNumber: "EMP001",
    employeeType: "Full-time",
    department: "Finance",
    status: "Completed",
    lastUpdated: "2026-02-15",
    completionDate: "2026-02-15",
    policies: ["Provident Fund", "Funeral Cover", "Death Benefit"],
  },
  {
    id: "2",
    name: "Michael Brown",
    employeeNumber: "EMP002",
    employeeType: "Part-time",
    department: "Operations",
    status: "Pending",
    lastUpdated: "2025-11-20",
    policies: ["Funeral Cover"],
  },
  {
    id: "3",
    name: "Emily Davis",
    employeeNumber: "EMP003",
    employeeType: "Full-time",
    department: "HR",
    status: "Completed",
    lastUpdated: "2026-01-28",
    completionDate: "2026-01-28",
    policies: ["Provident Fund", "Funeral Cover", "Death Benefit"],
  },
  {
    id: "4",
    name: "James Wilson",
    employeeNumber: "EMP004",
    employeeType: "Part-time",
    department: "Operations",
    status: "Overdue",
    lastUpdated: "2025-08-10",
    policies: ["Funeral Cover"],
  },
  {
    id: "5",
    name: "Lisa Anderson",
    employeeNumber: "EMP005",
    employeeType: "Full-time",
    department: "Finance",
    status: "Overdue",
    lastUpdated: "2025-09-05",
    policies: ["Provident Fund", "Funeral Cover", "Death Benefit"],
  },
  {
    id: "6",
    name: "David Martinez",
    employeeNumber: "EMP006",
    employeeType: "Part-time",
    department: "Operations",
    status: "Completed",
    lastUpdated: "2026-02-10",
    completionDate: "2026-02-10",
    policies: ["Funeral Cover"],
  },
];

// ─── Stats ───────────────────────────────────────────────────────────────────

const stats = {
  total: employeesData.length,
  completed: employeesData.filter((e) => e.status === "Completed").length,
  pending: employeesData.filter((e) => e.status === "Pending").length,
  overdue: employeesData.filter((e) => e.status === "Overdue").length,
};

// ─── Employee Row Component ──────────────────────────────────────────────────

interface EmployeeRowProps {
  employee: Employee;
  isLast: boolean;
  onSendReminder: (id: string) => void;
  onViewForm: (id: string) => void;
}

function EmployeeRow({ employee, isLast, onSendReminder, onViewForm }: EmployeeRowProps) {
  const getStatusIcon = () => {
    switch (employee.status) {
      case "Completed":
        return <CheckCircle2 size={20} className="text-green-600" />;
      case "Pending":
        return <Clock size={20} className="text-amber-500" />;
      case "Overdue":
        return <AlertCircle size={20} className="text-red-600" />;
    }
  };

  const getStatusColor = () => {
    switch (employee.status) {
      case "Completed":
        return "text-green-600";
      case "Pending":
        return "text-amber-500";
      case "Overdue":
        return "text-red-600";
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between py-md">
        <div className="flex items-center gap-sm flex-1">
          <div className="flex items-center justify-center w-[40px] h-[40px] rounded-full bg-secondary-pink-100 shrink-0">
            {getStatusIcon()}
          </div>

          <div className="flex-1">
            <div className="flex items-center gap-xxs mb-xxxs">
              <span className="text-sm font-semibold text-greyscale-text-title">
                {employee.name}
              </span>
              <span className="text-xs text-greyscale-text-caption">
                ({employee.employeeNumber})
              </span>
              <span className="inline-flex items-center gap-xxxs rounded-full bg-secondary-pink-100 px-xs py-xxxs text-xs font-semibold text-greyscale-text-title">
                {employee.employeeType}
              </span>
            </div>
            <div className="flex items-center gap-xs text-xs text-greyscale-text-caption">
              <span>{employee.department}</span>
              <span>•</span>
              <span className={getStatusColor()}>{employee.status}</span>
              {employee.lastUpdated && (
                <>
                  <span>•</span>
                  <span>Last updated: {employee.lastUpdated}</span>
                </>
              )}
            </div>
            <div className="flex items-center gap-xxxs mt-xxxs">
              {employee.policies.map((policy) => (
                <span
                  key={policy}
                  className="text-xs text-greyscale-text-body px-xs py-[2px] rounded-full border border-greyscale-border"
                >
                  {policy}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-xxs">
          {employee.status === "Completed" && (
            <Button
              variant="outline"
              size="small"
              leftIcon={<FileText size={16} />}
              onClick={() => onViewForm(employee.id)}
            >
              View Form
            </Button>
          )}
          {(employee.status === "Pending" || employee.status === "Overdue") && (
            <Button
              variant="outline"
              size="small"
              leftIcon={<Send size={16} />}
              onClick={() => onSendReminder(employee.id)}
            >
              Send Reminder
            </Button>
          )}
        </div>
      </div>
      {!isLast && <Divider variant="secondary" />}
    </div>
  );
}

// ─── Stats Card Component ────────────────────────────────────────────────────

interface StatsCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
}

function StatsCard({ title, value, icon, color }: StatsCardProps) {
  return (
    <div className="rounded-lg border border-greyscale-border bg-white p-md">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-regular text-greyscale-text-caption mb-xxxs">
            {title}
          </p>
          <h3 className="text-2xl font-semibold text-greyscale-text-title">
            {value}
          </h3>
        </div>
        <div className={`rounded-full p-xs ${color}`}>{icon}</div>
      </div>
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export function BeneficiaryForms() {
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [employeeTypeFilter, setEmployeeTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const handleSendReminder = (id: string) => {
    console.log("Sending reminder to employee:", id);
    // Implement reminder logic
  };

  const handleViewForm = (id: string) => {
    console.log("Viewing form for employee:", id);
    // Implement view form logic
  };

  const handleBulkReminder = () => {
    console.log("Sending bulk reminders");
    // Implement bulk reminder logic
  };

  const handleExport = () => {
    console.log("Exporting data");
    // Implement export logic
  };

  // Filter employees based on active tab and filters
  const filteredEmployees = employeesData.filter((employee) => {
    // Tab filter
    if (activeTab === "completed" && employee.status !== "Completed") return false;
    if (activeTab === "pending" && employee.status !== "Pending") return false;
    if (activeTab === "overdue" && employee.status !== "Overdue") return false;

    // Search filter
    if (
      searchQuery &&
      !employee.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
      !employee.employeeNumber.toLowerCase().includes(searchQuery.toLowerCase())
    ) {
      return false;
    }

    // Employee type filter
    if (employeeTypeFilter !== "all" && employee.employeeType !== employeeTypeFilter) {
      return false;
    }

    // Status filter
    if (statusFilter !== "all" && employee.status !== statusFilter) {
      return false;
    }

    return true;
  });

  return (
    <div>
      {/* Header */}
      <div
        className="flex items-center justify-between -mx-xl -mt-lg px-xl py-lg mb-md"
        style={{ backgroundColor: "rgba(237,244,251,0.5)" }}
      >
        <div>
          <h1 className="font-heading text-2xl font-semibold leading-36 text-greyscale-text-title">
            Beneficiary Forms
          </h1>
          <p className="py-[6px] text-sm font-regular leading-18 text-greyscale-text-body">
            Track employee completion of beneficiary forms for Funeral Cover, Provident/Pension
            Fund, and Death Benefit.
          </p>
        </div>
        <div className="flex items-center gap-xxs">
          <Button
            variant="outline"
            leftIcon={<Download size={16} />}
            onClick={handleExport}
          >
            Export
          </Button>
          <Button leftIcon={<Send size={16} />} onClick={handleBulkReminder}>
            Send Bulk Reminder
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-4 gap-md mb-md">
        <StatsCard
          title="Total Employees"
          value={stats.total}
          icon={<FileText size={20} className="text-primary-navy-700" />}
          color="bg-primary-navy-100"
        />
        <StatsCard
          title="Completed"
          value={stats.completed}
          icon={<CheckCircle2 size={20} className="text-green-600" />}
          color="bg-green-100"
        />
        <StatsCard
          title="Pending"
          value={stats.pending}
          icon={<Clock size={20} className="text-amber-500" />}
          color="bg-amber-100"
        />
        <StatsCard
          title="Overdue"
          value={stats.overdue}
          icon={<AlertCircle size={20} className="text-red-600" />}
          color="bg-red-100"
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList variant="line" className="w-full justify-start">
          <TabsTrigger value="all" variant="line">
            All Employees ({stats.total})
          </TabsTrigger>
          <TabsTrigger value="completed" variant="line">
            Completed ({stats.completed})
          </TabsTrigger>
          <TabsTrigger value="pending" variant="line">
            Pending ({stats.pending})
          </TabsTrigger>
          <TabsTrigger value="overdue" variant="line">
            Overdue ({stats.overdue})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab}>
          {/* Filters */}
          <div className="flex items-center gap-sm mt-md mb-sm">
            <div className="flex-1">
              <InputField
                placeholder="Search by name or employee number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="w-[200px]">
              <Select value={employeeTypeFilter} onValueChange={setEmployeeTypeFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Employee Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="Full-time">Full-time</SelectItem>
                  <SelectItem value="Part-time">Part-time</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-[200px]">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="Overdue">Overdue</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <BranchFilter
            branches={["All Branches", "Finance", "HR", "Operations"]}
            className="mb-md"
          />

          {/* Employee List */}
          <div className="rounded-lg border border-greyscale-border bg-white">
            {filteredEmployees.length > 0 ? (
              <div className="p-sm">
                {filteredEmployees.map((employee, index) => (
                  <EmployeeRow
                    key={employee.id}
                    employee={employee}
                    isLast={index === filteredEmployees.length - 1}
                    onSendReminder={handleSendReminder}
                    onViewForm={handleViewForm}
                  />
                ))}
              </div>
            ) : (
              <div className="p-lg text-center">
                <p className="text-sm text-greyscale-text-caption">
                  No employees found matching your filters.
                </p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
